import chroma from 'chroma-js';
import { useAtomValue } from 'jotai';
import React, { useState } from 'react';
import { Collapse } from 'reactstrap';

import { getDrillDownUrl } from './helper';
import {
  historyChartDataAtom,
  historyChartLabelsAtom,
  showHistoryChartCountsAtom,
  intervalAtom,
  policyIdFilterAtom,
  criticalityFiltersAtom,
  appliedHostIdsAtom,
} from './state';
import HistoryChart from '../components/HistoryChart';
import { getSeverityFilterValues } from '../helper';

import '../styles.css';

const HistoryCharts = () => {
  const policyId = useAtomValue(policyIdFilterAtom);
  const severityFilters = useAtomValue(criticalityFiltersAtom);
  const historyChartLabels = useAtomValue(historyChartLabelsAtom);
  const historyChartData = useAtomValue(historyChartDataAtom);
  const showHistoryChartCounts = useAtomValue(showHistoryChartCountsAtom);
  const interval = useAtomValue(intervalAtom);
  const appliedHostIds = useAtomValue(appliedHostIdsAtom);

  const [expandedChartKey, setExpandedChartKey] = useState<string | null>(null);

  const toggleChartExpansion = (chartKey: string) => {
    setExpandedChartKey((prevKey) => (prevKey === chartKey ? null : chartKey));
  };

  function getHistoryDrilldownUrl(status?: string) {
    // validate status value
    if (status?.toLowerCase() == 'all') {
      status = undefined;
    }
    return getDrillDownUrl({
      policyId: policyId,
      status_in: status ? [status] : undefined,
      criticality_in: getSeverityFilterValues(severityFilters),
      hosts_in: appliedHostIds,
    });
    return '';
  }

  const colorScale = (value: number) =>
    chroma
      .scale([
        '#B85050', // 0%  - Muted Red (risk)
        '#C5685C', // 14% - Red-Orange
        '#D67E6A', // 28% - Coral
        '#E59677', // 42% - Soft Salmon
        '#F0B086', // 56% - Peach/Apricot (transition pivot)
        '#7DBFA7', // 70% - Fresh Green-Teal
        '#5FB3B3', // 84% - Teal
        '#52ADAD', // 100% - Deep Teal (target)
      ])
      .mode('lab')
      .domain([0, 14, 28, 42, 56, 70, 84, 100])(value)
      .hex();

  const complianceDataSet = historyChartData?.slice(3, 4);

  const getChartPropertiesDictionary = () => ({
    all: {
      title: 'Findings',
      labels: historyChartLabels,
      tooltipLabels: [
        { label: 'Passed', hostsCount: null },
        { label: 'Failed', hostsCount: null },
      ],
      datasets: historyChartData?.slice(0, 2),
      colors: ['#5FB3B3', '#B85050'],
      selectedInterval: interval,
      showCounts: showHistoryChartCounts,
      onExpandClick: () => toggleChartExpansion('all'),
      getDrillDownUrl: (sourceType) => getHistoryDrilldownUrl(sourceType), // Restore this attribute
      newDataGenerationIndex: 0,
    },
    assets: {
      title: 'Assets',
      labels: historyChartLabels,
      tooltipLabels: [{ label: 'Assets', hostsCount: null }],
      // if more then 1 appliedHostIds then don't show, else show only the first one
      datasets: appliedHostIds?.length > 1 ? [] : historyChartData?.slice(2, 3),
      colors: ['#0096FF'],
      selectedInterval: interval,
      showCounts: showHistoryChartCounts,
      onExpandClick: () => toggleChartExpansion('assets'),
      getDrillDownUrl: () => getHistoryDrilldownUrl(), // Restore this attribute
      newDataGenerationIndex: 0,
    },
    compliance: {
      title: '% Compliance Score',
      labels: historyChartLabels,
      tooltipLabels: [{ label: 'Compliance Score', hostsCount: null }],
      datasets: complianceDataSet,
      colors:
        complianceDataSet && complianceDataSet.length > 0
          ? complianceDataSet.map((dataset) => {
              if (!dataset || dataset.length === 0) {
                return '#D3D3D3'; // Default color (Light Gray) if dataset is empty
              }
              const lastValue = dataset[dataset.length - 1];
              return colorScale(lastValue);
            })
          : ['#D3D3D3'], // Default colors array when complianceDataSet is empty
      colorScale,
      selectedInterval: interval,
      showCounts: showHistoryChartCounts,
      onExpandClick: () => toggleChartExpansion('compliance'),
      getDrillDownUrl: () => getHistoryDrilldownUrl(), // Restore this attribute
      newDataGenerationIndex: 0,
    },
  });

  let chartPropertiesDictionary;
  if (historyChartData) {
    chartPropertiesDictionary = getChartPropertiesDictionary();
  }

  return (
    <>
      {historyChartData && (
        <div className="row">
          <Collapse isOpen={!!expandedChartKey} className="col-12">
            {expandedChartKey && (
              <HistoryChart
                key="expandedChart"
                {...chartPropertiesDictionary[expandedChartKey]}
                isActive={true}
              />
            )}
          </Collapse>
          <div className="col-md-6">
            <HistoryChart
              {...chartPropertiesDictionary['all']}
              isActive={expandedChartKey === 'all'}
            />
          </div>
          {!(appliedHostIds?.length > 1) && (
            <div className="col-md-3">
              <HistoryChart
                {...chartPropertiesDictionary['assets']}
                isActive={expandedChartKey === 'assets'}
              />
            </div>
          )}
          <div className="col-md-3">
            <HistoryChart
              {...chartPropertiesDictionary['compliance']}
              isActive={expandedChartKey === 'compliance'}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default HistoryCharts;
